export interface ImageResult {
    src: string;
    success: boolean;
    dimensions?: { width: number; height: number };
    error?: string;
}

export interface ImageLoaderOptions {
    images: HTMLImageElement[];
    retries?: number;
    retryDelay?: number;
}

export function createImageLoader(options: ImageLoaderOptions) {
    const { images, retries = 3, retryDelay = 200 } = options;

    function createImagePromise(
        img: HTMLImageElement,
        attemptCount = 0
    ): Promise<ImageResult> {
        return new Promise((resolve) => {
            // If image is already loaded, resolve immediately with dimensions
            if (img.complete && img.naturalWidth > 0) {
                return resolve({
                    src: img.src,
                    success: true,
                    dimensions: {
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    },
                });
            }

            const handleLoad = () => {
                cleanup();
                resolve({
                    src: img.src,
                    success: true,
                    dimensions: {
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    },
                });
            };

            const handleError = () => {
                cleanup();

                if (attemptCount < retries) {
                    setTimeout(() => {
                        createImagePromise(img, attemptCount + 1).then(resolve);
                    }, retryDelay);
                } else {
                    // After all retries fail, resolve with error information
                    resolve({
                        src: img.src,
                        success: false,
                        error: `Failed to load after ${retries} attempts`,
                    });
                }
            };

            function cleanup() {
                img.removeEventListener('load', handleLoad);
                img.removeEventListener('error', handleError);
            }

            img.addEventListener('load', handleLoad, { once: true });
            img.addEventListener('error', handleError, { once: true });
        });
    }

    async function loadImages(): Promise<ImageResult[]> {
        if (!images.length) {
            return [];
        }

        try {
            const settledPromises = await Promise.allSettled(
                images.map((img) => createImagePromise(img))
            );

            // Convert the PromiseSettledResult array to ImageResult array
            return settledPromises.map((result) => {
                if (result.status === 'fulfilled') {
                    return result.value;
                } else {
                    return {
                        src: 'unknown',
                        success: false,
                        error: result.reason || 'Unknown error',
                    };
                }
            });
        } catch (error) {
            console.error('Unexpected error in image loader:', error);
            return [];
        }
    }

    return {
        load: loadImages,
    };
}
